import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { ThemeEvents, PageRenderedEvent } from '@theme/events';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute('shuffle-variants')) {
      if (!this.#hasActiveFiltersOrSort()) this.#shuffleVariantCards();
      document.addEventListener(ThemeEvents.pageRendered, this.#handlePageRendered);
    }
    if (this.hasAttribute('shuffle-variants-manual')) {
      if (!this.#hasActiveFiltersOrSort()) this.#sortVariantCardsManually();
      document.addEventListener(ThemeEvents.pageRendered, this.#handlePageRenderedManual);
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    if (this.hasAttribute('shuffle-variants')) {
      document.removeEventListener(ThemeEvents.pageRendered, this.#handlePageRendered);
    }
    if (this.hasAttribute('shuffle-variants-manual')) {
      document.removeEventListener(ThemeEvents.pageRendered, this.#handlePageRenderedManual);
    }
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #hasActiveFiltersOrSort() {
    const url = new URL(window.location.href);
    const hasFilters = [...url.searchParams.keys()].some((key) => key.startsWith('filter.'));
    const hasSort = url.searchParams.has('sort_by');
    return hasFilters || hasSort;
  }

  /** @param {Event} event */
  #handlePageRendered = (event) => {
    if (this.#hasActiveFiltersOrSort()) return;
    const pageRenderedEvent = /** @type {PageRenderedEvent} */ (event);
    const newVariantItems = /** @type {HTMLElement[]} */ (
      pageRenderedEvent.detail.items.filter((node) => node instanceof HTMLElement && node.dataset.variantId)
    );
    if (newVariantItems.length) this.#shuffleVariantCards(newVariantItems);
  };

  #sortVariantCardsManually() {
    const variantItems = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-variant-id]')]);
    if (!variantItems.length) return;

    const sorted = [...variantItems].sort((itemA, itemB) => {
      const orderA = itemA.hasAttribute('data-order') ? Number(itemA.dataset.order) : Infinity;
      const orderB = itemB.hasAttribute('data-order') ? Number(itemB.dataset.order) : Infinity;
      return orderA - orderB;
    });

    const placeholders = variantItems.map((variantItem) => {
      const placeholder = document.createComment('');
      variantItem.parentNode?.insertBefore(placeholder, variantItem);
      return placeholder;
    });

    for (let i = 0; i < sorted.length; i++) {
      const placeholder = placeholders[i];
      const variantItem = sorted[i];
      if (placeholder && variantItem) {
        placeholder.parentNode?.replaceChild(variantItem, placeholder);
      }
    }
  }

  /** @param {Event} event */
  #handlePageRenderedManual = (event) => {
    if (this.#hasActiveFiltersOrSort()) return;
    const pageRenderedEvent = /** @type {PageRenderedEvent} */ (event);
    const hasNewVariants = pageRenderedEvent.detail.items.some(
      (node) => node instanceof HTMLElement && node.dataset.variantId
    );
    if (hasNewVariants) this.#sortVariantCardsManually();
  };

  /**
   * @param {HTMLElement[]} [variantItems]
   */
  #shuffleVariantCards(variantItems = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-variant-id]')])) {
    if (!variantItems.length) return;

    const placeholders = variantItems.map((variantItem) => {
      const placeholder = document.createComment('');
      variantItem.parentNode?.insertBefore(placeholder, variantItem);
      return placeholder;
    });

    for (let i = variantItems.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const itemI = variantItems[i];
      const itemJ = variantItems[j];
      if (itemI && itemJ) {
        variantItems[i] = itemJ;
        variantItems[j] = itemI;
      }
    }

    for (let i = 0; i < variantItems.length; i++) {
      const placeholder = placeholders[i];
      const variantItem = variantItems[i];
      if (placeholder && variantItem) {
        placeholder.parentNode?.replaceChild(variantItem, placeholder);
      }
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
